import { useMemo } from 'react'
import { components } from 'react-select'
import ReactSelect from 'react-select'

const borderBlue = '1px solid rgba(64, 121, 245, 0.5)'
const textPrimary = '#272424'
const textMuted = '#666666'

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 37,
    maxHeight: 37,
    minWidth: 0,
    width: '100%',
    fontSize: 14,
    color: textPrimary,
    backgroundColor: '#ffffff',
    border: borderBlue,
    boxShadow: state.isFocused ? '0 0 0 1px rgba(64, 121, 245, 0.35)' : base.boxShadow,
    '&:hover': {
      border: borderBlue,
      backgroundColor: '#ffffff'
    }
  }),
  valueContainer: base => ({
    ...base,
    padding: '0 8px',
    height: 35,
    overflow: 'hidden',
    color: textPrimary
  }),
  singleValue: base => ({
    ...base,
    marginLeft: 0,
    marginRight: 0,
    maxWidth: 'calc(100% - 4px)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: textPrimary
  }),
  placeholder: base => ({
    ...base,
    color: textMuted,
    marginLeft: 0
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
    color: textPrimary
  }),
  indicatorsContainer: base => ({
    ...base,
    height: 35,
    color: textMuted
  }),
  dropdownIndicator: base => ({
    ...base,
    padding: 4,
    color: textMuted,
    ':hover': {
      color: textPrimary
    }
  }),
  clearIndicator: base => ({
    ...base,
    padding: 4
  }),
  menu: base => ({
    ...base,
    zIndex: 20,
    fontSize: 14,
    minWidth: 'min(100%, 280px)'
  }),
  menuList: base => ({
    ...base,
    maxHeight: 320
  }),
  menuPortal: base => ({
    ...base,
    zIndex: 10000
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    padding: '8px 10px',
    lineHeight: 1.35,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    color: textPrimary,
    backgroundColor: state.isSelected ? '#e8f0fe' : state.isFocused ? '#f0f5ff' : '#ffffff',
    fontWeight: state.isSelected ? 600 : 400,
    ':active': {
      backgroundColor: '#dce8fc'
    }
  })
}

const OptionWithTitle = props => {
  const label = props.data?.label != null ? String(props.data.label) : ''
  return (
    <components.Option {...props}>
      <span title={label}>{props.children}</span>
    </components.Option>
  )
}

const SingleValueWithTitle = props => {
  const label = props.data?.label != null ? String(props.data.label) : ''
  return (
    <components.SingleValue {...props}>
      <span title={label} style={{ color: textPrimary }}>
        {props.children}
      </span>
    </components.SingleValue>
  )
}

/**
 * Single-select with typeahead for Health Check filters. Matches filter bar height and border.
 */
const HealthCheckSearchableSelect = ({ value, options, onChange, className }) => {
  const selectOptions = useMemo(() => {
    const keys = Object.keys(options || {})
    const sorted = keys.sort((a, b) => {
      if (a === 'all') {
        return -1
      }
      if (b === 'all') {
        return 1
      }
      const la = String(options[a] || '')
      const lb = String(options[b] || '')
      return la.localeCompare(lb, undefined, { sensitivity: 'base' })
    })
    return sorted.map(k => ({ value: k, label: options[k] }))
  }, [options])

  const selected = selectOptions.find(o => o.value === value) || null

  return (
    <div className={className}>
      <ReactSelect
        classNamePrefix="hc-searchable-select"
        isSearchable
        isClearable={false}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        menuPosition="fixed"
        components={{ Option: OptionWithTitle, SingleValue: SingleValueWithTitle }}
        placeholder="Search or pick..."
        noOptionsMessage={({ inputValue }) =>
          inputValue ? `No matches for "${inputValue}"` : 'No options'
        }
        styles={selectStyles}
        value={selected}
        options={selectOptions}
        onChange={opt => {
          if (opt) {
            onChange(opt.value)
          }
        }}
      />
    </div>
  )
}

export default HealthCheckSearchableSelect
